import React, { useEffect, useMemo, useRef, useState } from 'react';

import { GST_RATES, PAYER_TYPE_LABELS, CHARGE_PERMISSIONS, CHARGE_ERROR_CODE, ADMISSION_STATUS, dodIsConfirmed, isBedDayChargeItem, istCalendarDate } from '../../../constants/admission';
import { formatPaise, formatRupees, todayYmd } from '../../../utils/money';
import { usePermission } from '../../../context/PermissionContext';
import { formatDateIST } from '../../../utils/formatIST';
import { listChargeItems } from '../../../services/api/chargeItemsApi';

const PICKER_VISIBLE_ITEMS = 8;

const overlayCls = 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4';
const panelCls = 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl w-full max-w-md max-h-[92vh] overflow-y-auto';
const btnPrimary = 'px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold disabled:opacity-60';
const btnSecondary = 'px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm';
const inputCls = 'w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm';
const labelCls = 'block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1';
const readOnlyChip = 'inline-flex items-center text-[12px] font-semibold px-2.5 py-1 rounded-full border border-teal-400 bg-teal-100 dark:bg-teal-900/40 text-teal-800 dark:text-teal-200';

/** A fresh idempotency key per SAVE (not once per dialog open — fix per S2 AC3). */
function newKey(prefix) {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return `${prefix}:${crypto.randomUUID()}`;
  return `${prefix}:${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** ISO ("…T…+05:30") → "YYYY-MM-DD": the date portion is already org-local (IST). */
function ymdOf(iso) {
  return typeof iso === 'string' && iso.length >= 10 ? iso.slice(0, 10) : null;
}

/** S2 AC3 range-hint copy (verbatim) — null when the item is unbounded (hint hidden). */
function rangeHintText(min, max) {
  if (min != null && max != null) return `Allowed: ${formatRupees(min)}–${formatRupees(max)}`;
  if (min != null) return `Allowed: ${formatRupees(min)} or more`;
  if (max != null) return `Allowed: ${formatRupees(max)} or less`;
  return null;
}

/** S2 AC4 disabled-save / inline out-of-range copy (verbatim). */
function boundsMessage(min, max) {
  if (min != null && max != null) return `Enter an amount between ${formatRupees(min)} and ${formatRupees(max)}.`;
  if (min != null) return `Enter ${formatRupees(min)} or more.`;
  if (max != null) return `Enter ${formatRupees(max)} or less.`;
  return 'Enter an amount above ₹0.';
}

/**
 * S2 AC7 — maps a 422 `PRICE_OUT_OF_RANGE` onto the amount field using the
 * RETURNED bounds (paise, from the error's extras), never the client's
 * possibly-stale copy of the item's range.
 */
function priceOutOfRangeMessage(minPricePaise, maxPricePaise) {
  const min = minPricePaise != null ? minPricePaise / 100 : null;
  const max = maxPricePaise != null ? maxPricePaise / 100 : null;
  if (min != null && max != null) return `This item’s allowed amount changed. Allowed now: ${formatRupees(min)}–${formatRupees(max)}.`;
  if (min != null) return `This item’s allowed amount changed. Allowed now: ${formatRupees(min)} or more.`;
  if (max != null) return `This item’s allowed amount changed. Allowed now: ${formatRupees(max)} or less.`;
  return 'This item’s allowed amount changed. Enter an amount above ₹0.';
}

/**
 * QA-D1 fix — a `PRICE_OUT_OF_RANGE` 422 also carries the item's CURRENT
 * `variable_price` flag (the server's disambiguator). If it disagrees with
 * the dialog's cached row (an admin flipped the item while the dialog was
 * open), the amount-vs-range message above is meaningless — render the
 * ratified RE-PICK copy instead. `stories.md` "Microcopy — ratified" §1
 * (verbatim, no auto-reload variant); the (b) string is SUPERSEDED by the
 * round-2 term ruling ("billing desk", never "the desk").
 */
function stalePriceItemMessage(serverIsVariable) {
  return serverIsVariable
    ? 'That item’s price changed — the billing desk now types the amount. Pick it again and enter an amount.'
    : 'That item’s price changed — it now has a fixed price. Pick it again to load the current price.';
}

/**
 * AddChargeDialog (day-1 S2) — add a priced line to the running bill. Leads
 * with a searchable CHARGE-ITEMS picker: picking an item auto-fills its name
 * and shows price + GST as READ-ONLY chips (server-priced — the client never
 * types or sends a rate). Qty defaults 1. "Save & add another" posts the line
 * and keeps the dialog open, cleared, focused on search, with a FRESH
 * idempotency key per save (the old per-open ref replayed on a repeat save —
 * fixed here). "Save & close" posts and closes.
 *
 * The legacy free-text one-off charge path is admin-only (MANAGE_CHARGE_MASTER
 * — server also gates the route on the same slug; this hide is cosmetic on
 * top) via an "Add a one-off charge" link that swaps the item search for
 * description/rate/GST fields; toggling back clears whichever side is hidden.
 *
 * Save is disabled-until-valid + single-flight (busy guard) so a double-click
 * posts once. Rendered via the in-app modal — no window.confirm.
 *
 * Bed-day hint (bed-charging switch-off, FE-2): picking a seeded ROOM_BED
 * charge item shows a non-binding "suggested rate" derived from the
 * admission's `frozen_tariff` (passed in via `frozenTariff` — already on the
 * running-bill payload the caller has, no new fetch) plus the owner-locked
 * bed-day helper text. The server-priced chip above stays the authoritative
 * amount; this is guidance only.
 *
 * Variable-price amount (OPD-CHARGE-RANGE FE-2, S2): a `variable_price` item
 * replaces the read-only price chip with an editable "Amount (₹) *" input,
 * prefilled from the item's suggested price (`price > 0`), else empty +
 * focused. A range hint (`min_price`/`max_price`, rupees) shows under it;
 * Save is disabled with a reason while the amount is empty/out of range.
 * The server is the fence (§1.5) — the client sends `unit_price_paise`
 * (`Math.round(₹ * 100)`) only for a variable item, never for a fixed one.
 * A `PRICE_OUT_OF_RANGE` 422 maps inline onto the amount using the
 * RETURNED paise bounds, not the (possibly stale) client copy. Changing the
 * item clears the amount, hint and any error.
 */
export default function AddChargeDialog({ admission, balancePaise, frozenTariff, onClose, onSubmitItem, onSubmitFreeText }) {
  const isAdmin = usePermission(CHARGE_PERMISSIONS.MANAGE_CHARGE_MASTER);
  const [mode, setMode] = useState('picker'); // 'picker' | 'freetext'

  const minDate = ymdOf(admission.occupied_at) || ymdOf(admission.admitted_at) || null;
  const today = todayYmd();
  // Once discharge is initiated the confirmed date is final: no charge after it.
  // IST calendar day (server compares `local_date_of`), never a UTC slice —
  // a DOD between IST midnight and 05:30 is the PREVIOUS UTC day.
  const dischargeYmd = (admission.status === ADMISSION_STATUS.DISCHARGE_INITIATED
    || admission.status === ADMISSION_STATUS.BILLING_CLEARANCE) && dodIsConfirmed(admission)
    ? istCalendarDate(admission.planned_discharge_at) : null;
  const maxDate = dischargeYmd && dischargeYmd < today ? dischargeYmd : today;
  const dischargeMsg = (ymd) => `Service date can't be after the discharge date (${formatDateIST(`${ymd}T00:00:00+05:30`)}).`;

  // ── Charge items (fetched once — bounded, no per-render growth) ─────────
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [itemsError, setItemsError] = useState(null);
  const fetchedRef = useRef(false);
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    (async () => {
      const res = await listChargeItems({ activeOnly: true });
      if (res.success) setItems(res.data.items || []);
      else setItemsError(res.error || 'Could not load charge items.');
      setItemsLoading(false);
    })();
  }, []);

  // ── Picker state ──────────────────────────────────────────────────────
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [qty, setQty] = useState('1');
  const [serviceDate, setServiceDate] = useState(maxDate);
  const [itemIdemKey, setItemIdemKey] = useState(() => newKey('item'));
  const searchInputRef = useRef(null);

  // ── Variable-price amount (FE-2, S2) ──────────────────────────────────
  const [amount, setAmount] = useState('');
  const [amountServerError, setAmountServerError] = useState(null);
  // QA-D1: item's variable_price flipped server-side while the dialog was
  // open (client's cached row disagrees with the 422's `variable_price`).
  // Save stays disabled until the user re-picks the item — cleared in
  // selectItem, never by re-typing the amount.
  const [staleItemMismatch, setStaleItemMismatch] = useState(false);

  // ── Free-text (admin one-off) state ──────────────────────────────────
  const [description, setDescription] = useState('');
  const [ftQty, setFtQty] = useState('1');
  const [rate, setRate] = useState('');
  const [gst, setGst] = useState(0);
  const [ftServiceDate, setFtServiceDate] = useState(maxDate);
  const [ftIdemKey, setFtIdemKey] = useState(() => newKey('manual'));

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const noticeTimerRef = useRef(null);
  useEffect(() => () => { if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current); }, []);

  const flashNotice = (msg) => {
    setNotice(msg);
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = setTimeout(() => setNotice(null), 2500);
  };

  /** Selecting/clearing an item — prefills the amount from a configured
   * suggested price (> 0), else starts empty; always clears the amount,
   * hint and any error (client or server-mapped) per S2 AC9. */
  const selectItem = (it) => {
    setSelectedItem(it);
    setAmountServerError(null);
    setError(null);
    setStaleItemMismatch(false);
    if (it && it.variable_price) {
      const suggested = Number(it.price);
      setAmount(suggested > 0 ? String(suggested) : '');
    } else {
      setAmount('');
    }
  };

  // ── Picker: filter + long-list truncation (R10) ──────────────────────
  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => (it.name || '').toLowerCase().includes(q) || (it.category || '').toLowerCase().includes(q));
  }, [items, query]);
  const visibleItems = showAll ? filteredItems : filteredItems.slice(0, PICKER_VISIBLE_ITEMS);
  const hiddenItemCount = Math.max(0, filteredItems.length - visibleItems.length);

  const qtyNum = Number(qty);
  const qtyValid = Number.isInteger(qtyNum) && qtyNum >= 1;
  const dateValid = !!serviceDate && (!minDate || serviceDate >= minDate) && serviceDate <= maxDate;

  // ── Variable-price amount fence (client convenience — server is the fence,
  // S2 AC6): min/max are RUPEE bounds off the selected item (§1.3 contract);
  // inclusive at both ends. Empty/non-positive/out-of-bounds is invalid.
  const isVariableItem = !!(selectedItem && selectedItem.variable_price);
  // A stored 0 minimum (legacy pre-normalisation row) means "no minimum" —
  // never rendered as a real floor, mirrors the server-side rule.
  const minPrice = selectedItem && selectedItem.min_price != null && Number(selectedItem.min_price) > 0
    ? Number(selectedItem.min_price) : null;
  const maxPrice = selectedItem && selectedItem.max_price != null ? Number(selectedItem.max_price) : null;
  const amountNum = amount === '' ? NaN : Number(amount);
  const amountRangeInvalid = isVariableItem && (
    !Number.isFinite(amountNum) || amountNum <= 0
    || (minPrice != null && amountNum < minPrice)
    || (maxPrice != null && amountNum > maxPrice)
  );
  const rangeHint = isVariableItem ? rangeHintText(minPrice, maxPrice) : null;

  const itemValid = !!selectedItem && qtyValid && dateValid && !staleItemMismatch && (!isVariableItem || !amountRangeInvalid);

  const itemPreviewPaise = useMemo(() => {
    if (!selectedItem || !qtyValid) return 0;
    const unitRupees = isVariableItem ? (Number.isFinite(amountNum) ? amountNum : 0) : (Number(selectedItem.price) || 0);
    const unitPricePaise = Math.round(unitRupees * 100);
    const gross = qtyNum * unitPricePaise;
    return gross + Math.round((gross * (Number(selectedItem.gst_rate) || 0)) / 100);
  }, [selectedItem, qtyNum, qtyValid, isVariableItem, amountNum]);

  // ── Bed-day hint (FE-2): suggested rate = room + nursing + RMO, read
  // straight off the `frozenTariff` prop already sitting on the running-bill
  // payload — no backend call. Null when the admission has no frozen tariff
  // yet (e.g. no bed assigned); the helper text still shows on its own.
  const isBedItem = isBedDayChargeItem(selectedItem);
  const suggestedBedRatePaise = useMemo(() => {
    if (!frozenTariff) return null;
    const perDay = Number(frozenTariff.per_day_paise) || 0;
    const nursing = Number(frozenTariff.nursing_per_day_paise) || 0;
    const rmo = Number(frozenTariff.rmo_per_day_paise) || 0;
    return perDay + nursing + rmo;
  }, [frozenTariff]);

  // ── Free-text validation + preview (legacy path, unchanged math) ─────
  const ftQtyNum = Number(ftQty);
  const ftUnitPricePaise = Math.round((Number.isFinite(Number(rate)) ? Number(rate) : 0) * 100);
  const ftQtyValid = Number.isInteger(ftQtyNum) && ftQtyNum >= 1;
  const ftRateValid = Number.isFinite(Number(rate)) && Number(rate) > 0;
  const ftDateValid = !!ftServiceDate && (!minDate || ftServiceDate >= minDate) && ftServiceDate <= maxDate;
  const ftValid = description.trim().length > 0 && ftQtyValid && ftRateValid && ftDateValid;
  const ftPreviewPaise = useMemo(() => {
    if (!ftQtyValid || !ftRateValid) return 0;
    const gross = ftQtyNum * ftUnitPricePaise;
    return gross + Math.round((gross * gst) / 100);
  }, [ftQtyNum, ftUnitPricePaise, gst, ftQtyValid, ftRateValid]);

  const valid = mode === 'picker' ? itemValid : ftValid;

  const resetItemFields = () => {
    setSelectedItem(null);
    setQty('1');
    setQuery('');
    setAmount('');
    setAmountServerError(null);
    setStaleItemMismatch(false);
    setItemIdemKey(newKey('item'));
    if (searchInputRef.current) searchInputRef.current.focus();
  };
  const resetFreeTextFields = () => {
    setDescription('');
    setFtQty('1');
    setRate('');
    setGst(0);
    setFtIdemKey(newKey('manual'));
  };

  const toggleToFreeText = () => {
    setMode('freetext');
    setError(null);
  };
  const toggleToPicker = () => {
    resetFreeTextFields();
    setMode('picker');
    setError(null);
  };

  const errorCopy = (res, fallback) => {
    switch (res.code) {
      case CHARGE_ERROR_CODE.CHARGE_ITEM_INACTIVE:
        return 'That item is no longer available. Refresh the list and pick another.';
      case CHARGE_ERROR_CODE.CHARGE_ITEM_NOT_FOUND:
        return 'That item could not be found. Refresh the list and pick another.';
      case CHARGE_ERROR_CODE.SERVICE_DATE_AFTER_DISCHARGE:
        return res.discharge_date ? dischargeMsg(res.discharge_date) : res.error || fallback;
      case 'SERVICE_DATE_OUT_OF_WINDOW':
        return 'That date is outside this admission’s stay. Pick a date within the stay.';
      default:
        return res.error || fallback;
    }
  };

  const submitItem = async (closeAfter) => {
    if (!itemValid || busy) return;
    setBusy(true);
    setError(null);
    setAmountServerError(null);
    const body = {
      item_id: selectedItem.item_id,
      qty: qtyNum,
      service_date: serviceDate,
      idempotency_key: itemIdemKey,
    };
    if (isVariableItem) body.unit_price_paise = Math.round(amountNum * 100);
    const res = await onSubmitItem(body);
    setBusy(false);
    if (res && res.success) {
      if (closeAfter) { onClose(); return; }
      resetItemFields();
      flashNotice('Charge added — balance updated.');
      return;
    }
    if (res) {
      if (res.code === CHARGE_ERROR_CODE.PRICE_OUT_OF_RANGE) {
        const serverIsVariable = !!res.variable_price;
        if (serverIsVariable !== isVariableItem) {
          // QA-D1: the cached row's variable_price disagrees with the
          // server's CURRENT flag — the range/missing-amount message would
          // be nonsense here. Render the RE-PICK copy; Save stays disabled
          // (staleItemMismatch) until the user re-picks the item. The
          // amount field is only mounted when isVariableItem is true, so a
          // mismatch with no amount field renders as the dialog banner.
          setStaleItemMismatch(true);
          const msg = stalePriceItemMessage(serverIsVariable);
          if (isVariableItem) setAmountServerError(msg);
          else setError(msg);
        } else {
          setAmountServerError(priceOutOfRangeMessage(res.min_price_paise, res.max_price_paise));
        }
      } else {
        setError(errorCopy(res, 'Could not add the charge. Check the values and try again.'));
      }
    }
  };

  const submitFreeText = async (closeAfter) => {
    if (!ftValid || busy) return;
    setBusy(true);
    setError(null);
    const res = await onSubmitFreeText({
      description: description.trim(),
      unit_price_paise: ftUnitPricePaise,
      qty: ftQtyNum,
      gst_rate: Number(gst),
      service_date: ftServiceDate,
      idempotency_key: ftIdemKey,
    });
    setBusy(false);
    if (res && res.success) {
      if (closeAfter) { onClose(); return; }
      resetFreeTextFields();
      flashNotice('Charge added — balance updated.');
      return;
    }
    if (res) setError(errorCopy(res, 'Could not add the charge. Check the values and try again.'));
  };

  const submit = (closeAfter) => (mode === 'picker' ? submitItem(closeAfter) : submitFreeText(closeAfter));

  const payerLabel = PAYER_TYPE_LABELS[admission.payer_type] || admission.payer_type || 'Not set';
  const saveDisabledReason = mode === 'picker'
    ? (!selectedItem
      ? 'Pick a charge item to add.'
      : staleItemMismatch
        ? (amountServerError || error)
        : (isVariableItem && amountRangeInvalid ? boundsMessage(minPrice, maxPrice) : ''))
    : (!description.trim() ? 'Enter a description and a rate above ₹0 to save.' : !ftRateValid ? 'Enter a rate above ₹0 to save.' : '');

  return (
    <div className={overlayCls} data-testid="add-charge-dialog">
      <div className={panelCls}>
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Add charge</h3>
          <button type="button" onClick={onClose} className="ml-auto text-slate-500 dark:text-slate-400" aria-label="Close">✕</button>
        </div>
        <div className="p-4 space-y-3">
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-[12.5px] text-slate-600 dark:text-slate-300 px-3 py-2">
            <b className="text-slate-900 dark:text-white">{admission.patient_name || admission.patient_id}</b> · {admission.ip_number}
            {balancePaise != null && <> · Balance so far <b className="text-slate-900 dark:text-white">{formatPaise(balancePaise)}</b></>}
          </div>

          {notice && (
            <div className="rounded-lg border border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20 text-teal-800 dark:text-teal-200 text-[12.5px] p-2.5" data-testid="add-charge-notice">{notice}</div>
          )}
          {error && (
            <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="add-charge-error">{error}</div>
          )}

          {mode === 'picker' ? (
            <>
              <div>
                <label className={labelCls} htmlFor="ac-search">Charge item</label>
                <input
                  id="ac-search"
                  ref={searchInputRef}
                  className={inputCls}
                  type="text"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setShowAll(false); if (selectedItem) selectItem(null); }}
                  placeholder="Search charge items…"
                  data-testid="charge-item-search"
                  autoFocus
                />
              </div>

              {selectedItem ? (
                <>
                  <div className="rounded-lg border border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20 px-3 py-2.5 flex items-center gap-2 flex-wrap" data-testid="selected-charge-item">
                    <b className="text-slate-900 dark:text-white">{selectedItem.name}</b>
                    {!isVariableItem && (
                      <span className={readOnlyChip} data-testid="selected-item-price">₹{Number(selectedItem.price).toLocaleString('en-IN')}</span>
                    )}
                    <span className={readOnlyChip} data-testid="selected-item-gst">{Number(selectedItem.gst_rate) > 0 ? `${selectedItem.gst_rate}% GST` : 'No GST'}</span>
                    <button type="button" className="ml-auto text-[12px] font-semibold text-teal-700 dark:text-teal-300 underline" onClick={() => selectItem(null)} data-testid="clear-selected-item">Change item</button>
                  </div>
                  {isVariableItem && (
                    <div className="rounded-lg border border-teal-300 dark:border-teal-700 bg-white dark:bg-slate-800 px-3 py-2.5 mt-2" data-testid="variable-amount-block">
                      <label className={labelCls} htmlFor="ac-amount">Amount (₹) *</label>
                      <input
                        id="ac-amount"
                        className={`${inputCls} ${(amountServerError || (amount !== '' && amountRangeInvalid)) ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : ''}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={amount}
                        onChange={(e) => { setAmount(e.target.value); setAmountServerError(null); }}
                        placeholder="0"
                        autoFocus={amount === ''}
                        data-testid="charge-amount"
                      />
                      {rangeHint && (
                        <div className={`text-[11.5px] mt-1 ${amountRangeInvalid ? 'text-slate-500 dark:text-slate-400' : 'text-teal-700 dark:text-teal-300'}`} data-testid="charge-amount-hint">{rangeHint}</div>
                      )}
                      {(amountServerError || (amount !== '' && amountRangeInvalid)) && (
                        <div className="text-[12px] text-red-600 dark:text-red-400 font-semibold mt-1" data-testid="charge-amount-error">
                          {amountServerError || boundsMessage(minPrice, maxPrice)}
                        </div>
                      )}
                    </div>
                  )}
                  {isBedItem && (
                    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2.5 flex items-start gap-2 mt-3" data-testid="bed-day-hint">
                      <span aria-hidden="true" title="Bed-day charge guidance" className="flex-none text-[12px] text-slate-500 dark:text-slate-400">ⓘ</span>
                      <div className="text-[12px] text-slate-600 dark:text-slate-300 space-y-1">
                        {suggestedBedRatePaise != null && (
                          <div data-testid="bed-day-suggested-rate">
                            Suggested: <b className="text-slate-900 dark:text-white">{formatPaise(suggestedBedRatePaise)}/day</b> (room + nursing + RMO)
                          </div>
                        )}
                        <div data-testid="bed-day-helper-text">1 bed-day per 24h from admission; class = bed occupied at census cut-off.</div>
                      </div>
                    </div>
                  )}
                </>
              ) : itemsLoading ? (
                <div className="text-[12.5px] text-slate-500 dark:text-slate-400" data-testid="charge-items-loading">Loading charge items…</div>
              ) : itemsError ? (
                <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="charge-items-load-error">{itemsError}</div>
              ) : filteredItems.length === 0 ? (
                <div className="text-[12.5px] text-slate-500 dark:text-slate-400" data-testid="charge-items-empty">
                  No matching charge items. Ask an admin to add it to the list.
                  {isAdmin && (
                    <> <button type="button" className="text-teal-700 dark:text-teal-300 underline font-semibold" onClick={toggleToFreeText} data-testid="empty-add-one-off">…or add a one-off charge.</button></>
                  )}
                </div>
              ) : (
                <div className="rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-200 dark:divide-slate-700 max-h-56 overflow-y-auto" data-testid="charge-item-results">
                  {visibleItems.map((it) => (
                    <button
                      key={it.item_id}
                      type="button"
                      onClick={() => selectItem(it)}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-[13px] text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700"
                      data-testid={`charge-item-${it.item_id}`}
                    >
                      <span className="truncate">{it.name}</span>
                      <span className="text-[11.5px] text-slate-500 dark:text-slate-400 flex-none">{it.variable_price ? 'Varies' : `₹${Number(it.price).toLocaleString('en-IN')}`}{Number(it.gst_rate) > 0 ? ` · ${it.gst_rate}%` : ''}</span>
                    </button>
                  ))}
                  {hiddenItemCount > 0 && (
                    <button type="button" onClick={() => setShowAll(true)} className="w-full px-3 py-2 text-left text-[11.5px] font-semibold text-teal-700 dark:text-teal-300 hover:bg-slate-50 dark:hover:bg-slate-700" data-testid="charge-items-more">Show all {filteredItems.length} (+{hiddenItemCount} more)</button>
                  )}
                </div>
              )}

              <div className="flex gap-3">
                <div className="flex-1">
                  <label className={labelCls} htmlFor="ac-qty">Qty</label>
                  <input id="ac-qty" className={inputCls} type="number" min="1" step="1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="charge-qty" />
                </div>
                <div className="flex-1">
                  <label className={labelCls} htmlFor="ac-date">Date of service</label>
                  <input id="ac-date" className={inputCls} type="date" value={serviceDate} min={minDate || undefined} max={maxDate} onChange={(e) => setServiceDate(e.target.value)} />
                  {dischargeYmd && serviceDate > dischargeYmd && (
                    <p role="alert" data-testid="charge-date-after-discharge" className="mt-1 text-xs text-red-600">{dischargeMsg(dischargeYmd)}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2.5">
                <span className="text-[13px] text-slate-600 dark:text-slate-300">Preview total</span>
                <b className="text-[15px] tabular-nums text-slate-900 dark:text-white" data-testid="add-charge-preview">{formatPaise(itemPreviewPaise)}</b>
              </div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">Final amount is confirmed by the organisation&rsquo;s saved price when you save.</p>

              {isAdmin && (
                <button type="button" className="text-[12.5px] font-semibold text-teal-700 dark:text-teal-300 underline" onClick={toggleToFreeText} data-testid="open-freetext">Add a one-off charge</button>
              )}
            </>
          ) : (
            <>
              <button type="button" className="text-[12.5px] font-semibold text-teal-700 dark:text-teal-300 underline" onClick={toggleToPicker} data-testid="back-to-picker">← Back to charge items</button>

              <div>
                <label className={labelCls} htmlFor="ac-desc">Description</label>
                <input id="ac-desc" className={inputCls} type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Labour room charges" maxLength={200} />
              </div>

              <div className="flex gap-3">
                <div className="flex-1">
                  <label className={labelCls} htmlFor="ac-ft-qty">Quantity</label>
                  <input id="ac-ft-qty" className={inputCls} type="number" min="1" step="1" value={ftQty} onChange={(e) => setFtQty(e.target.value)} />
                </div>
                <div className="flex-1">
                  <label className={labelCls} htmlFor="ac-rate">Rate per unit (₹)</label>
                  <input id="ac-rate" className={inputCls} type="number" min="0" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="0" />
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-1">
                  <label className={labelCls} htmlFor="ac-gst">GST</label>
                  <select id="ac-gst" className={inputCls} value={gst} onChange={(e) => setGst(Number(e.target.value))}>
                    {GST_RATES.map((r) => <option key={r} value={r}>{r === 0 ? 'None' : `${r}%`}</option>)}
                  </select>
                </div>
                <div className="flex-1">
                  <label className={labelCls} htmlFor="ac-ft-date">Date of service</label>
                  <input id="ac-ft-date" className={inputCls} type="date" value={ftServiceDate} min={minDate || undefined} max={maxDate} onChange={(e) => setFtServiceDate(e.target.value)} />
                  {dischargeYmd && ftServiceDate > dischargeYmd && (
                    <p role="alert" data-testid="charge-date-after-discharge-ft" className="mt-1 text-xs text-red-600">{dischargeMsg(dischargeYmd)}</p>
                  )}
                </div>
              </div>

              <div>
                <label className={labelCls}>Payer</label>
                <span className="inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300">{payerLabel} — set on the admission</span>
              </div>

              <div className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2.5">
                <span className="text-[13px] text-slate-600 dark:text-slate-300">Preview total</span>
                <b className="text-[15px] tabular-nums text-slate-900 dark:text-white" data-testid="add-charge-preview">{formatPaise(ftPreviewPaise)}</b>
              </div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">The final amount is confirmed by the server when you save.</p>
            </>
          )}
        </div>
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2 flex-wrap">
          <button type="button" className={btnSecondary} onClick={onClose}>Cancel</button>
          <button
            type="button"
            className={btnSecondary}
            onClick={() => submit(false)}
            disabled={!valid || busy}
            title={!valid ? saveDisabledReason : undefined}
            data-testid="add-charge-save-another"
          >
            {busy ? 'Saving…' : 'Save & add another'}
          </button>
          <button
            type="button"
            className={btnPrimary}
            onClick={() => submit(true)}
            disabled={!valid || busy}
            title={!valid ? saveDisabledReason : undefined}
            data-testid="add-charge-save-close"
          >
            {busy ? 'Saving…' : 'Save & close'}
          </button>
        </div>
      </div>
    </div>
  );
}
